import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatMoneyLedger } from "../lib/formatMoney";
import { pluralKey } from "../lib/pluralKey";
import { Loader2, Search } from "lucide-react";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import IdCell from "./IdCell";
import HorizontalScroller from "./HorizontalScroller";
import { DateTimeCell, PracticeCell } from "./LedgerCells";
import TenantPicker from "./TenantPicker";
import DateRangeFilter, { isRangeInvalid } from "./DateRangeFilter";
import { SEARCH_MAX_LENGTH, useDebouncedSearch } from "./useDebouncedSearch";
import { usePagedList } from "./usePagedList";

const PAGE_SIZE = 25;
// The statuses the SaaS ledger writers store; the server refuses anything else.
const STATUSES = ["initiated", "completed", "failed", "expired", "refunded", "refund_unverified"];
const FIELD_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

// The ledger stores amounts in major units (49.99); the shared formatter takes minor units.
// The ledger style prints the ISO code, as the client ledger does: CAD, USD and INR rows
// share this column and its totals, and a "$" would not say which dollar.
const money = (amount, currency, lang) => formatMoneyLedger(Math.round(Number(amount || 0) * 100), currency, lang);

const TAG_CLASS = "ml-2 inline-block whitespace-nowrap px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600";
// Dense ledger cells: the table has to fit a laptop screen's content box.
const CELL = "px-4 py-3";
const HEAD = "px-4 py-4";

// A promotion run, from the data its row carries (`promotion_name`, `promotion_days`), in the
// console's language. A legacy row the migration could not explain has neither; it still carries
// the English sentence it was written with, which is never printed: it gets the bare label.
function promotionLabel(tr, t, lang) {
  if (!tr.promotion_name) return t("tx.promotionUnnamed");
  const n = Number(tr.promotion_days);
  return t(pluralKey(lang, n, "tx.promotion"), { name: tr.promotion_name, n });
}

// Plan column = what the row billed, as the server resolved it: the plan's display name; else the
// booster's (a booster subscription) with a tag; else, for a one-time purchase, what was bought (a
// promotion run, or the SKU's stored catalogue name) with a tag; else the raw plan id (a deleted
// plan shows as what it is); else a dash.
function planCell(tr, t, lang) {
  if (tr._plan_name) return tr._plan_name;
  if (tr._booster_name) {
    return (
      <>
        {tr._booster_name}
        <span className={TAG_CLASS}>{t("tx.booster")}</span>
      </>
    );
  }
  if (tr.is_one_time) {
    return (
      <>
        {tr.promotion_id ? promotionLabel(tr, t, lang) : tr.description}
        <span className={TAG_CLASS}>{t("tx.oneTime")}</span>
      </>
    );
  }
  if (tr.plan_id) return <span className="font-mono text-xs text-slate-500">{tr.plan_id}</span>;
  return "—";
}

// Why a completed row is worth nothing (`zero_charge`): a free item, or an invoice that charged
// nothing (a card-required trial's first invoice, a full discount). Neither is a payment.
function zeroChargeBadge(tr, t) {
  if (tr.zero_charge === "free_item") {
    return <span className={TAG_CLASS} data-testid={`tx-free-${tr.row_key}`}>{t("tx.free")}</span>;
  }
  if (tr.zero_charge === "no_charge") {
    return (
      <span className={TAG_CLASS} title={t("tx.noChargeHint")} data-testid={`tx-nocharge-${tr.row_key}`}>
        {t("tx.noCharge")}
      </span>
    );
  }
  return null;
}

export default function TransactionsList() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch, q] = useDebouncedSearch(() => setPage(1));
  // Bumped by Retry, so the all-time totals are re-read together with the page.
  const [summaryTick, setSummaryTick] = useState(0);

  // The server refuses an inverted or impossible range, so it is never sent.
  const rangeInvalid = isRangeInvalid(from, to);
  const { data: result, totalForCurrent, loading, loadFailed, retry } = usePagedList({
    path: "/super-admin/payment-transactions",
    filters: { q, status, tenant_id: tenantId, from, to },
    page, setPage, pageSize: PAGE_SIZE, enabled: !rangeInvalid,
  }); // result: { total, items }

  useEffect(() => {
    // Platform-wide totals on their own endpoint: they are never narrowed by the filters
    // (the footer says so), so they are read once, not per page.
    api.get("/super-admin/payment-transactions/summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [summaryTick]);

  // A filter change always starts again at the first page.
  const filter = (setter) => (value) => { setter(value); setPage(1); };

  const currencies = Object.keys(summary?.net_by_currency || {});

  return (
    <div data-testid="sa-transactions">
      <h1 className="font-sol font-semibold text-4xl mb-2">{t("tx.title")}</h1>
      {/* Only a total read for the filters on screen is printed: not before it lands, not after
          a failed read, and not for an inverted range (never sent, so no total answers it). */}
      <p className="text-slate-500 mb-6">{totalForCurrent === null ? t("tx.subtitleBare") : t("tx.subtitle", { n: totalForCurrent })}</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("tx.searchPh")}
            aria-label={t("tx.searchPh")}
            className={`${FIELD_CLASS} w-full pl-10 placeholder:text-slate-500`}
            data-testid="tx-search"
            maxLength={SEARCH_MAX_LENGTH}
          />
        </div>
        <select
          value={status}
          onChange={(e) => filter(setStatus)(e.target.value)}
          className={FIELD_CLASS}
          aria-label={t("tx.colStatus")}
          data-testid="tx-status"
        >
          <option value="">{t("tx.allStatuses")}</option>
          {STATUSES.map((v) => <option key={v} value={v}>{t(`tx.status.${v}`)}</option>)}
        </select>
        <TenantPicker
          value={tenantId}
          onChange={filter(setTenantId)}
          allowAll
          className="w-64"
          testId="tx-tenant"
        />
        <DateRangeFilter
          from={from}
          to={to}
          onFromChange={filter(setFrom)}
          onToChange={filter(setTo)}
          testIds={{ from: "tx-from", to: "tx-to", error: "tx-range-error" }}
        />
      </div>

      <LoadFailure
        message={loadFailed && !loading && !rangeInvalid ? tc("errors.loadFailed") : ""}
        onRetry={() => { retry(); setSummaryTick((n) => n + 1); }}
        retryLabel={t("sal.retry")}
        testId="tx-load-error"
        retryTestId="tx-load-error-retry"
      />
      {loading && !rangeInvalid && (loadFailed || !result) && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {!loadFailed && !rangeInvalid && result && (
        <>
        <p className="text-xs text-slate-500 mb-2" data-testid="tx-refund-help">{t("tx.refundHelp")}</p>
        <div
          className={`rounded-2xl border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`}
          aria-busy={loading}
          data-testid="tx-table"
        >
          {/* The table fills its box: text cells (practice, plan) wrap, money, dates, statuses
              and references do not. Only when even that is wider than the box does it scroll,
              with an edge cue saying so. The pager stays outside the scroller so it never
              scrolls away; the bordered box clips both (and the footer's corners) to its
              rounding. */}
          <HorizontalScroller testId="tx-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider text-left">
              <tr>
                <th className={HEAD}>{t("tx.colDate")}</th>
                <th className={HEAD}>{t("tx.colTenant")}</th>
                <th className={HEAD}>{t("tx.colPlan")}</th>
                <th className={HEAD}>{t("tx.colAmount")}</th>
                <th className={HEAD}>{t("tx.colStatus")}</th>
                <th className={HEAD}>{t("tx.colRefundRef")}</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((tr) => {
                const refunded = Number(tr.refunded_amount || 0);
                return (
                  <tr key={tr.row_key} className="border-t border-slate-100 hover:bg-white">
                    <td className={`${CELL} text-slate-500`}><DateTimeCell value={tr.created_at} lang={lang} /></td>
                    <td className={CELL}><PracticeCell name={tr._tenant_name} id={tr.tenant_id} /></td>
                    <td className={CELL}>{planCell(tr, t, lang)}</td>
                    <td className={`${CELL} whitespace-nowrap`}>
                      {/* NET first — what the platform kept. A partially refunded charge
                          used to render its gross amount with nothing to say otherwise. */}
                      {money(tr.net_amount ?? tr.amount, tr.currency, lang)}
                      {zeroChargeBadge(tr, t)}
                      {refunded > 0 && (
                        <span className="ml-2 text-xs text-rose-600" data-testid={`tx-refunded-${tr.row_key}`}>
                          −{money(refunded, tr.currency, lang)}
                        </span>
                      )}
                    </td>
                    <td className={`${CELL} whitespace-nowrap`}>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        tr.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                        tr.status === "initiated" ? "bg-amber-100 text-amber-700" :
                        "bg-rose-100 text-rose-700"
                      }`}>{t(`tx.status.${tr.status}`, undefined, tr.status)}</span>
                    </td>
                    {/* `refund_ref` is the server's answer to "what does the refund form accept for
                        this row": null unless the charge is refundable, so a session id (which the
                        form never searches) is never offered here. */}
                    <td className={`${CELL} whitespace-nowrap`}><IdCell value={tr.refund_ref} width="max-w-[9rem]" /></td>
                  </tr>
                );
              })}
              {result.items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="tx-empty">{t("tx.empty")}</td></tr>
              )}
            </tbody>
            {currencies.length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={6} className="px-4 pt-3 text-xs text-slate-500" data-testid="tx-totals-note">{t("tx.allTimeTotals")}</td>
                </tr>
                {currencies.map((cur) => (
                  <tr key={cur} data-testid={`tx-total-${cur}`}>
                    <td className={`${CELL} font-semibold text-slate-700`} colSpan={3} data-testid={`tx-total-label-${cur}`}>
                      {t("tx.totalLabel", { cur: cur.toUpperCase() })}
                    </td>
                    <td className={`${CELL} font-semibold whitespace-nowrap`}>
                      {money(summary.net_by_currency[cur], cur, lang)}
                      {Number(summary.refunded_by_currency?.[cur] || 0) > 0 && (
                        <span className="ml-2 text-xs text-rose-600">
                          −{money(summary.refunded_by_currency[cur], cur, lang)}
                        </span>
                      )}
                    </td>
                    <td className={CELL} colSpan={2} />
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
          </HorizontalScroller>
          {result.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={result.total}
              shown={result.items.length}
              onPage={setPage}
              infoText={(v) => t("sal.pagerInfo", v)}
              prevLabel={t("sal.pagerPrev")}
              nextLabel={t("sal.pagerNext")}
              testId="tx-pager"
              tone="slate"
            />
          )}
        </div>
        </>
      )}
    </div>
  );
}
